import { getTreeData } from 'vet-bones/utils';
import {
  QueryBuilderClass,
  QueryBuilderProperty,
} from 'vet-bones/utils/queryBuilder';

import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import {
  ProjectModelClass,
  ProjectModelProperty,
  ProjectModelRule,
} from 'src/store/state/projects';
import { ModelRulesBindingResult } from 'src/templates/queries/modelRules';
import { parseModelRuleResults } from 'src/utils/binding/parseModelRuleResults';

/**
 * Builds project model rules from query bindings.
 * This constructs the QueryBuilder model needed for rule parsing,
 * including class hierarchy and property relationships.
 */
export const buildProjectModelRules = ({
  bindings,
  classes,
  colorIdsByIri,
  databaseNamespaces,
  labelsByIri,
  modelName,
  properties,
}: {
  bindings: ModelRulesBindingResult[];
  classes: Record<string, ProjectModelClass> | null;
  colorIdsByIri: Record<string, string> | null;
  databaseNamespaces: Record<string, string>;
  labelsByIri: Record<string, string> | null;
  modelName: string;
  properties: Record<string, ProjectModelProperty> | null;
}): Record<string, ProjectModelRule> => {
  const outgoingProperties: Record<string, Set<string>> = {};
  const incomingProperties: Record<string, Set<string>> = {};
  const propertiesByIri: Record<string, QueryBuilderProperty> = {};
  const classesByIri: Record<string, QueryBuilderClass> = {};
  const parentsByClassIri: Record<string, Set<string>> = {};

  const addClassIriToProperties =
    (property: string, properties: Record<string, Set<string>>) =>
    (classIri: string) => {
      if (!properties[classIri]) {
        // eslint-disable-next-line no-param-reassign
        properties[classIri] = new Set();
      }
      properties[classIri].add(property);
    };

  // add properties
  Object.values(properties || {}).forEach((data) => {
    data.domain.forEach(addClassIriToProperties(data.iri, outgoingProperties));
    data.range.forEach(addClassIriToProperties(data.iri, incomingProperties));
    propertiesByIri[data.iri] = {
      iri: data.iri,
      label: getLabelForIri(labelsByIri || {}, data.iri),
      isDatatype: Boolean(data.isDatatype),
      domains: new Set(data.domain),
      ranges: new Set(data.range),
    };
  });

  // add classes
  Object.values(classes || {}).forEach((data) => {
    classesByIri[data.iri] = {
      iri: data.iri,
      label: getLabelForIri(labelsByIri || {}, data.iri),
      color: '',
      colorId: colorIdsByIri?.[data.iri] || 'sd-qb-black',
      designerColorId: '',
      ancestors: new Set(),
      descendants: new Set(),
      incomingProperties: new Set(incomingProperties[data.iri]),
      outgoingProperties: new Set(outgoingProperties[data.iri]),
    };
    parentsByClassIri[data.iri] = new Set(data.parents);
  });

  // compute class hierarchy
  const { tree } = getTreeData(
    Object.keys(classesByIri).map((iri) => ({
      id: iri,
      parents: parentsByClassIri[iri],
    }))
  );

  // add class hierarchy data
  Object.keys(classesByIri).forEach((iri) => {
    const { ancestors, descendants } = tree[iri];
    classesByIri[iri].ancestors = ancestors;
    classesByIri[iri].descendants = descendants;

    // all ancestor properties also apply to this class
    ancestors.forEach((ancestor) => {
      incomingProperties[ancestor]?.forEach((propertyIri) => {
        classesByIri[iri].incomingProperties.add(propertyIri);
        propertiesByIri[propertyIri].ranges.add(iri);
      });
      outgoingProperties[ancestor]?.forEach((propertyIri) => {
        classesByIri[iri].outgoingProperties.add(propertyIri);
        propertiesByIri[propertyIri].domains.add(iri);
      });
    });
  });

  return parseModelRuleResults(
    bindings,
    modelName,
    { classesByIri, propertiesByIri },
    databaseNamespaces
  );
};
